import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import classNames from "classnames";
import { useLogout } from "@/hooks/useAuth";
import DrawerButton from "@assets/tsxIcons/DrawerButton";

import { appRoutes, QUERY_KEYS } from "@/constants";

import logoutLogo from "@assets/svgs/icon_logout.svg";
import logo from "@assets/svgs/Logo white.svg";
import setting from "@assets/svgs/setting1.svg";
import help from "@assets/svgs/icon_help.svg";
import { clearSentryUser } from "@utils/sentry";
import useGetData from "@/services/useGetData";
import { API_URLS } from "@/api/urls";
import { useFeaturesPopupStore } from "@/zustand/useFeaturesPopupStore";
import DownArrow from "@assets/tsxIcons/DownArrow";
import { useClickAway } from "react-use";
import { useCan } from "@/contexts/PermissionContext";
import useConfirmNavigation from "@/hooks/useConfirmNavigation";
import LockIcon from "@assets/tsxIcons/LockIcon";
import useSidebarStore from "@/zustand/useSidebarStore";

const AppSidebar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { has, hasAny } = useCan();

  const { setOpenReleasingPopup } = useFeaturesPopupStore();
  const {
    guardedNavigate,
    hasUnsavedChanges,
    setShowUnsavedChangesNotification,
  } = useConfirmNavigation();

  // Use Zustand store for sidebar collapse state
  const {
    isCollapsed: showNav,
    setIsCollapsed: setShowNav,
    toggleCollapsed: toggleShowNav,
  } = useSidebarStore();

  const [showVenuDropDown, setShowVenuDropDown] = useState<boolean>(false);
  const [showComingDropDown, setShowComingDropDown] = useState<boolean>(false);
  const venuDropDownRef = useRef<HTMLButtonElement>(null);
  const comingDropDownRef = useRef<HTMLDivElement>(null);

  useClickAway(venuDropDownRef, () => {
    if (showVenuDropDown) {
      setShowVenuDropDown(false);
    }
  });
  useClickAway(comingDropDownRef, () => {
    if (showComingDropDown) {
      setShowComingDropDown(false);
    }
  });

  const [showFullNav, setShowFullNav] = useState<boolean>(false);
  const [hoveredItemTitle, setHoveredItemTitle] = useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);

  const { mutate: logout, isPending } = useLogout();
  const { data: statsData } = useGetData<IsubscriptionStats>(
    [QUERY_KEYS.TOTAL_NEW_SUBMISSIONS],
    `${API_URLS.SUBMISSIONS.GET_SUBSCRIPTION_STATS}?type=total_new_submissions`,
    { refetchOnWindowFocus: true, staleTime: 0 }
  );

  // Fetch venue settings for dynamic venue name
  const { data: venueSettings } = useGetData<
    Array<{ name: string; id?: number }>
  >(["venue-settings"], API_URLS.VENUE.GET_VENUE_SETTINGS, {
    refetchOnWindowFocus: false,
  });

  // Handle responsive behavior
  useEffect(() => {
    const handleResize = () => {
      const isXlScreen = window.innerWidth >= 1440;
      // On mobile/tablet (< xl), always collapse
      if (!isXlScreen) {
        setShowNav(true);
        setShowVenuDropDown(false);
      }
    };

    window.addEventListener("resize", handleResize);
    handleResize(); // Call once on mount

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleNavigate = (path: string) => {
    guardedNavigate(path);
  };

  const handleLogout = () => {
    if (hasUnsavedChanges) {
      handleNavigate("/login");
      setShowUnsavedChangesNotification(true);
      return;
    }

    const refreshToken = localStorage.getItem("refreshToken");

    if (refreshToken) {
      logout(
        { refresh: refreshToken },
        {
          onSuccess: (response) => {
            if (response) {
              // Clear all React Query cache to ensure fresh data for next user
              queryClient.clear();
              queryClient.removeQueries();

              localStorage.removeItem("accessToken");
              localStorage.removeItem("refreshToken");
              clearSentryUser();
              navigate("/login");
            }
          },
          onError: (error) => {
            console.error("Logout Error:", error);
          },
        }
      );
    }
  };

  const toggleNav = () => {
    // Only allow toggle on xl screens and above
    if (window.innerWidth >= 1440) {
      toggleShowNav();
      setShowVenuDropDown(false);
    }
  };

  const handleMouseEnter = (
    title: string,
    event: React.MouseEvent<HTMLDivElement>
  ) => {
    // Only show tooltips when collapsed and on xl screens
    if (showNav && window.innerWidth >= 1440) {
      const rect = event.currentTarget.getBoundingClientRect();
      setTooltipPosition({
        top: rect.top + rect.height / 2,
        left: rect.right + 12,
      });
      setHoveredItemTitle(title);
    }
  };

  const handleMouseLeave = () => {
    setHoveredItemTitle(null);
    setTooltipPosition(null);
  };

  // Determine if we should show expanded content
  const isExpanded = !showNav && window.innerWidth >= 1440;
  const isCollapsed = showNav || window.innerWidth < 1440;

  const isDemo = window.location.href.includes("demo-admin");
  const NewSubmissionsCount = () => {
    if (
      statsData?.total_new_submissions &&
      statsData?.total_new_submissions > 0
    )
      return (
        <div className="new-submissions-badge z-[31]">
          <span className="text-[10px] font-semibold text-white">
            {statsData?.total_new_submissions}
          </span>
        </div>
      );
  };

  const getItemPermissionState = (
    title: string
  ): { hasPermission: boolean } => {
    // Check permissions for each menu item
    // NOTE: Resource names must match exactly what the API returns (case-sensitive before slug conversion)
    // API sends: "Customer Management", "Calendar Events", "Membership Plans" (plural), "Members", "Lesson & Coach"

    switch (title) {
      case "Dashboard":
        return {
          hasPermission: has({ resource: "Dashboard", action: "view" }),
        };
      case "Bookings":
        return { hasPermission: has({ resource: "Bookings", action: "view" }) };
      case "Events Calendar":
        // API sends "Calendar Events", not "Events"
        return {
          hasPermission: has({ resource: "Calendar Events", action: "view" }),
        };
      case "Customers":
        // API sends "Customer Management", not "Users"
        return {
          hasPermission: has({
            resource: "Customer Management",
            action: "view",
          }),
        };
      case "Memberships":
        return {
          hasPermission: hasAny([
            { resource: "Members", action: "view" }, // API sends "Members"
            { resource: "Membership Plans", action: "view" }, // API sends "Membership Plans" (plural)
          ]),
        };
      case "Transactions":
        // API sends "Transactions" (plural)
        return {
          hasPermission: has({ resource: "Transactions", action: "view" }),
        };
      case "Inbox":
        return {
          hasPermission: hasAny([
            { resource: "Inbox", action: "view" }, // Check parent "Inbox" permission first
            {
              resource: "Subscriptions",
              subResource: "event subscription",
              action: "view",
            },
            {
              resource: "Subscriptions",
              subResource: "newsletter contact form",
              action: "view",
            },
            {
              resource: "Subscriptions",
              subResource: "contact us manage",
              action: "view",
            },
          ]),
        };
      case "Analytics":
        return {
          hasPermission: has({ resource: "Analytics", action: "view" }),
        };
      case "Website CMS":
        // TODO: Enable permission check once backend grants "Website CMS" permission to Owner/Operator
        // return { hasPermission: has({ resource: "Website CMS", action: "view" }) };
        return { hasPermission: true }; // Temporarily allow access for all authenticated users
      case "Lessons & Fittings":
        // API sends "Lesson & Coach", not "Coaching"
        return {
          hasPermission: has({ resource: "Lesson & Coach", action: "view" }),
        };
      default:
        return { hasPermission: true }; // Unknown items allowed by default
    }
  };

  return (
    <nav
      className={`flex flex-col h-screen mr-3 flex-shrink-0 ${
        isCollapsed ? "w-[64px]" : "w-[220px]"
      } bg-gray1 transition-all duration-500 ease-in-out relative overflow-visible`}>
      <section className="ml-3 relative z-10">
        <div className="rounded-b-xl pb-[1.5px] relative z-50">
          <div className="pt-[26px] pb-[16px] flex flex-col items-center relative z-50 h-[75px]">
            <div
              className="cursor-pointer"
              onClick={() => {
                setOpenReleasingPopup(true);
              }}>
              {/* Always show full logo */}
              <img
                src={logo}
                className="[mix-blend-mode:screen]"
                alt="Logo"
                onClick={() => setShowFullNav(!showFullNav)}
              />
            </div>
          </div>

          {/* Company info section - only show when expanded */}
          {isExpanded && (
            <>
              <button
                ref={venuDropDownRef}
                onClick={() => setShowVenuDropDown(!showVenuDropDown)}
                className="group  mt-[17px] w-full">
                <div
                  className={`bg-[#307356] ${
                    showVenuDropDown ? "rounded-t-[10px]" : "rounded-[10px] "
                  }   flex items-center justify-between px-[12px] cursor-pointer z-50`}>
                  <div className="text-start">
                    <p className="text-[13px] py-2.5 leading-[18.15px]">
                      <span className="text-white font-medium">
                        {venueSettings?.[0]?.name ||
                          (isDemo
                            ? "Demo Golf & Sports Clubs"
                            : "Links Golf & Sports Clubs")}
                      </span>

                      <span className="text-white font-normal"> (11001)</span>
                    </p>
                  </div>

                  <DownArrow
                    className={`fill-[#96b8a9] group-hover:fill-wht transition-all delay-150 ${
                      showVenuDropDown ? "rotate-180" : ""
                    }`}
                  />
                </div>
              </button>

              <div
                className={`w-full absolute ${
                  showVenuDropDown
                    ? "max-h-fit opacity-100"
                    : "max-h-0 opacity-0"
                }  bg-[#307356] transition-all duration-300 ease-in-out overflow-hidden rounded-b-[10px] flex flex-col gap-1.5 justify-center items-center px-3`}>
                <button className="pt-4 pb-4 mb-4 bg-[#1DA46A] rounded-[6px] w-full mt-1.5">
                  <span className="text-white font-medium">
                    {venueSettings?.[0]?.name ||
                      (isDemo
                        ? "Demo Golf & Sports Clubs"
                        : "Links Golf & Sports Clubs")}
                  </span>

                  <span className="text-white font-normal"> (11001)</span>
                </button>
              </div>
            </>
          )}
        </div>
      </section>

      {/* Expand/Collapse toggle button - centered between sidebar and body */}
      <div
        className="absolute z-50 -right-[24px] transition-all duration-300 hidden xl:block top-[200px]"
        onClick={toggleNav}
        onMouseEnter={(e) =>
          handleMouseEnter(showNav ? "Expand Sidebar" : "Collapse Sidebar", e)
        }
        onMouseLeave={handleMouseLeave}>
        <div className="cursor-pointer hover:opacity-90 transition-opacity">
          <DrawerButton isCollapsed={isCollapsed} />
        </div>

        {/* Toggle button tooltip */}
        {hoveredItemTitle &&
          (hoveredItemTitle === "Expand Sidebar" ||
            hoveredItemTitle === "Collapse Sidebar") &&
          tooltipPosition && (
            <div
              className="fixed p-2 bg-[#293444] text-wht text-xs rounded shadow-xl drop-shadow-lg z-[9999] whitespace-nowrap pointer-events-none border border-gray-600"
              style={{
                top: `${tooltipPosition.top}px`,
                left: `${tooltipPosition.left}px`,
                transform: "translateY(-50%)",
              }}>
              {hoveredItemTitle}
            </div>
          )}
      </div>

      {/* Scrollable navigation section */}
      <section
        className={` flex-1 min-h-0 w-[104%] overflow-hidden   transition-all duration-500 ${
          isCollapsed
            ? "mt-8"
            : showVenuDropDown
            ? "pt-[90px] mt-4"
            : "overflow-x-hidden mt-4"
        }   `}>
        <div>
          {appRoutes.map((item, index) => {
            if (item.isComing) return null;

            const permissionState = getItemPermissionState(item.title);
            const isLocked = !permissionState.hasPermission;

            return (
              <div key={index}>
                <div
                  className={classNames("group rounded-xl ml-[10px] mr-[8%]", {
                    "bg-gray2":
                      !isLocked &&
                      location.pathname
                        .split("/")[1]
                        .includes(item.path.split("/")[1]),
                    "max-w-[266px]":
                      location.pathname === item.path && isExpanded,
                    "cursor-pointer hover:bg-gray2": !isLocked,
                    "cursor-not-allowed opacity-50": isLocked,
                  })}>
                  <section
                    className={classNames(" mx-auto", {
                      "max-w-[240px]": isExpanded,
                    })}>
                    <div
                      className="mt-[1px] py-[9px] px-[5px] relative"
                      onMouseEnter={(e) => handleMouseEnter(item.title, e)}
                      onMouseLeave={handleMouseLeave}>
                      <div
                        onClick={() => !isLocked && handleNavigate(item.path)}
                        className={classNames(
                          "flex items-center gap-x-[14px]",
                          isCollapsed ? "justify-center" : "justify-start",
                          { "pointer-events-none": isLocked }
                        )}>
                        <div className="relative">
                          {/* <img
                            src={item.image}
                            alt={item.title}
                            className="size-[32px]"
                          /> */}

                          <item.image
                            className={`${
                              location.pathname
                                .split("/")[1]
                                .includes(item.path.split("/")[1])
                                ? "fill-white"
                                : "fill-gray4"
                            } group-hover:fill-wht  size-[32px] transition-all duration-150`}
                          />
                          {item.title === "Inbox" && <NewSubmissionsCount />}
                        </div>
                        {isExpanded && (
                          <div
                            className={classNames(
                              "font-medium group-hover:text-wht text-[13px] flex text-start flex-col max-w-[161px] leading-[19.6px] font-inter transition-all duration-150",
                              {
                                "text-white":
                                  !isLocked &&
                                  location.pathname
                                    .split("/")[1]
                                    .includes(item.path.split("/")[1]),
                                "text-[#CED5E3] ":
                                  !isLocked &&
                                  !location.pathname
                                    .split("/")[1]
                                    .includes(item.path.split("/")[1]),
                                "text-gray-500": isLocked,
                              }
                            )}>
                            <span className="flex items-center gap-2">
                              {item.title}
                              {isLocked && (
                                <LockIcon className="size-3 text-gray-400" />
                              )}
                            </span>
                            <div className="font-normal"></div>
                          </div>
                        )}
                      </div>
                      {/* Hover tooltip - only show when collapsed */}
                      {isCollapsed &&
                        hoveredItemTitle === item.title &&
                        tooltipPosition && (
                          <div
                            className="fixed p-2 bg-[#293444] text-wht text-xs rounded shadow-xl drop-shadow-lg z-[9999] whitespace-nowrap pointer-events-none border border-gray-600"
                            style={{
                              top: `${tooltipPosition.top}px`,
                              left: `${tooltipPosition.left}px`,
                              transform: "translateY(-50%)",
                            }}>
                            {item.title}
                            {isLocked && " (Locked)"}
                          </div>
                        )}
                    </div>
                  </section>
                </div>
              </div>
            );
          })}
        </div>

        {isExpanded && (
          <>
            <div
              ref={comingDropDownRef}
              className={`relative w-full transition-all duration-300 ${
                showComingDropDown
                  ? "h-[calc(100%-675px)]"
                  : "h-[calc(100%-420px)]"
              }  flex items-end`}>
              <button
                onClick={() => setShowComingDropDown(!showComingDropDown)}
                className={`group relative py-3 z-[32] bg-gray1 " : " "
                 w-full`}>
                <div className="absolute h-[1px] bg-[#2A374B] w-[103.5%] mb-2 z-[32] top-0"></div>
                <div
                  className={` ${
                    showComingDropDown ? "rounded-t-[10px]" : "rounded-[10px] "
                  }   flex items-center justify-between px-[12px] cursor-pointer `}>
                  <div className="text-start mt-1">
                    <p className="text-[13px]  ml-[10px] leading-[18.15px]">
                      <span className="text-white font-semibold">
                        Coming soon
                      </span>
                    </p>
                  </div>

                  <DownArrow
                    className={`fill-[#96b8a9] mt-1 group-hover:fill-wht transition-all delay-150 mr-[12px] ${
                      showComingDropDown ? "rotate-180" : ""
                    }`}
                  />
                </div>
              </button>

              <div
                className={`transition-all ml-[10px] delay-300 duration-300 ${
                  showComingDropDown
                    ? "h-[278px] overflow-y-auto custom-scroll opacity-100 -bottom-[268px] pt-2"
                    : "max-h-0 opacity-0"
                } w-full absolute   `}>
                {appRoutes.map(
                  (item, index) =>
                    item.isComing && (
                      <div key={index}>
                        <div
                          className={classNames(
                            "rounded-xl mr-[10%] hover:bg-gray2"
                          )}>
                          <section
                            className={classNames(" mx-auto", {
                              "max-w-[240px]": isExpanded,
                            })}>
                            <div
                              className="mt-[1px] py-[9px] px-[5px] relative"
                              onMouseEnter={(e) =>
                                handleMouseEnter(item.title, e)
                              }
                              onMouseLeave={handleMouseLeave}>
                              <div
                                className={classNames(
                                  "flex cursor-pointer items-center gap-x-[14px]",
                                  isCollapsed
                                    ? "justify-center"
                                    : "justify-start"
                                )}>
                                <div className="relative">
                                  <item.image
                                    className={`${
                                      location.pathname
                                        .split("/")[1]
                                        .includes(item.path.split("/")[1])
                                        ? "fill-white"
                                        : "fill-gray4"
                                    } group-hover:fill-wht  size-[32px] transition-all duration-150`}
                                  />
                                </div>
                                {isExpanded && (
                                  <div
                                    className={classNames(
                                      "font-medium text-[13px] flex text-start flex-col max-w-[161px] leading-[19.6px] font-inter transition-all duration-500 ease-in-out delay-200",
                                      {
                                        " text-white": location.pathname
                                          .split("/")[1]
                                          .includes(item.path.split("/")[1]),
                                        "text-[#CED5E3] hover:text-wht":
                                          !location.pathname
                                            .split("/")[1]
                                            .includes(item.path.split("/")[1]),
                                      }
                                    )}>
                                    {item.title}
                                    <div className="font-normal"></div>
                                  </div>
                                )}
                              </div>
                              {/* Hover tooltip - only show when collapsed */}
                              {isCollapsed &&
                                hoveredItemTitle === item.title &&
                                tooltipPosition && (
                                  <div
                                    className="fixed p-2 bg-[#293444] text-wht text-xs rounded shadow-xl drop-shadow-lg z-[9999] whitespace-nowrap pointer-events-none border border-gray-600"
                                    style={{
                                      top: `${tooltipPosition.top}px`,
                                      left: `${tooltipPosition.left}px`,
                                      transform: "translateY(-50%)",
                                    }}>
                                    {item.title}
                                  </div>
                                )}
                            </div>
                          </section>
                        </div>
                      </div>
                    )
                )}
              </div>
            </div>
          </>
        )}
      </section>

      {/* Fixed bottom section - Settings and Logout */}
      <section className={` ${isCollapsed ? "" : ""} relative pt-1`}>
        <div className="absolute h-[2px] bg-[#2A374B] w-[103.5%] z-[32] top-0"></div>
        <div
          className={`text-wht rounded-xl flex justify-center ${
            isCollapsed ? "flex-col" : "flex-row"
          } `}>
          {/* Help */}
          <div
            className={classNames(
              "flex items-center justify-center py-[12px] rounded-xl hover:bg-gray2 relative",
              isCollapsed
                ? "justify-center px-3"
                : "px-[24px] gap-x-[14px] max-w-[266px]"
            )}
            onMouseEnter={(e) => handleMouseEnter("Help", e)}
            onMouseLeave={handleMouseLeave}>
            <Link
              target="_blank"
              to="https://help.teelogix.io/en"
              className="font-medium  text-[14px] leading-[19.6px] text-wht hover:text-green4">
              {" "}
              <img
                src={help}
                className="size-[32px] cursor-pointer min-h-[32px]"
                alt="Help"
              />
            </Link>

            {/* Help hover tooltip */}
            {isCollapsed && hoveredItemTitle === "Help" && tooltipPosition && (
              <div
                className="fixed p-2 bg-[#293444] text-wht text-xs rounded shadow-xl drop-shadow-lg z-[9999] whitespace-nowrap pointer-events-none border border-gray-600"
                style={{
                  top: `${tooltipPosition.top}px`,
                  left: `${tooltipPosition.left}px`,
                  transform: "translateY(-50%)",
                }}>
                Help
              </div>
            )}
          </div>
          {/* Settings */}
          <div
            className={classNames(
              "flex items-center py-[12px] rounded-xl hover:bg-gray2 relative",
              isCollapsed ? "justify-center px-3" : "px-[24px] gap-x-[14px]"
            )}
            onMouseEnter={(e) => handleMouseEnter("Settings", e)}
            onMouseLeave={handleMouseLeave}>
            <img
              onClick={() => handleNavigate("/settings/bookings")}
              src={setting}
              className="size-[32px] cursor-pointer min-h-[32px] min-w-[32px]"
              alt="Settings"
            />

            {/* Settings hover tooltip */}
            {isCollapsed &&
              hoveredItemTitle === "Settings" &&
              tooltipPosition && (
                <div
                  className="fixed p-2 bg-[#293444] text-wht text-xs rounded shadow-xl drop-shadow-lg z-[9999] whitespace-nowrap pointer-events-none border border-gray-600"
                  style={{
                    top: `${tooltipPosition.top}px`,
                    left: `${tooltipPosition.left}px`,
                    transform: "translateY(-50%)",
                  }}>
                  Settings
                </div>
              )}
          </div>

          {/* Logout - always visible */}
          <div
            className={classNames(
              "flex items-center py-[12px] rounded-xl hover:bg-gray2 relative",
              isCollapsed
                ? "justify-center px-3"
                : "px-[24px] gap-x-[14px] max-w-[266px]"
            )}
            onMouseEnter={(e) => handleMouseEnter("Logout", e)}
            onMouseLeave={handleMouseLeave}
            onClick={handleLogout}>
            <img
              src={logoutLogo}
              className="size-[32px] cursor-pointer min-h-[34px] min-w-[32px]"
              alt="Logout"
            />

            {/* Logout hover tooltip */}
            {isCollapsed &&
              hoveredItemTitle === "Logout" &&
              tooltipPosition && (
                <div
                  className="fixed p-2 bg-[#293444] text-wht text-xs rounded shadow-xl drop-shadow-lg z-[9999] whitespace-nowrap pointer-events-none border border-gray-600"
                  style={{
                    top: `${tooltipPosition.top}px`,
                    left: `${tooltipPosition.left}px`,
                    transform: "translateY(-50%)",
                  }}>
                  Logout
                  {isPending && <span className="ml-2">...</span>}
                </div>
              )}
          </div>
        </div>
      </section>
    </nav>
  );
};

export default AppSidebar;
